import { useState, useRef, useEffect } from "react";
import { BRAND } from "../constants/brand";

const CHAT_API_URL = process.env.REACT_APP_CHAT_API_URL || "http://localhost:8000";

const WELCOME = {
  role: "bot",
  text: "Hi! I'm the Labarium AI assistant. Ask me anything about our laboratory services, tests, insurance, appointments, or how to get your results.",
};

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([WELCOME]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [hasOpened, setHasOpened] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open, messages]);

  const toggle = () => {
    setOpen(o => !o);
    if (!hasOpened) setHasOpened(true);
  };

  const send = async () => {
    const q = input.trim();
    if (!q || loading) return;
    const userMsg = { role: "user", text: q };
    setMessages(prev => [...prev, userMsg]);
    setInput("");
    setLoading(true);

    const history = messages
      .filter(m => m !== WELCOME)
      .map(m => [m.role === "user" ? "human" : "ai", m.text]);

    try {
      const res = await fetch(`${CHAT_API_URL}/api/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ question: q, history }),
      });
      const data = await res.json();
      setMessages(prev => [...prev, { role: "bot", text: data.answer || "I wasn't able to find an answer. Please call (877) 765-2274." }]);
    } catch {
      setMessages(prev => [...prev, { role: "bot", text: "I'm having trouble connecting right now. Please call (877) 765-2274 or email info@labarium.com." }]);
    }
    setLoading(false);
  };

  const handleKey = e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } };

  return (
    <div style={{ position: "fixed", bottom: 24, right: 24, zIndex: 9999 }}>

      {/* Chat panel */}
      {open && (
        <div className="chat-panel" style={{
          position: "absolute",
          bottom: "calc(100% + 14px)",
          right: 0,
          width: 380,
          maxWidth: "calc(100vw - 48px)",
          height: 520,
          background: "rgba(0,14,36,0.97)",
          backdropFilter: "blur(28px)",
          WebkitBackdropFilter: "blur(28px)",
          borderRadius: 20,
          border: "1px solid rgba(201,169,110,0.2)",
          borderTop: `3px solid ${BRAND.gold}`,
          boxShadow: "0 32px 80px rgba(0,0,0,0.6), 0 0 0 1px rgba(201,169,110,0.08)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          animation: "chatSlideUp 0.3s cubic-bezier(.4,0,.2,1)",
        }}>

          {/* Header */}
          <div style={{
            padding: "14px 18px",
            borderBottom: "1px solid rgba(255,255,255,0.06)",
            display: "flex", alignItems: "center", gap: 12,
            background: "rgba(0,0,0,0.2)",
          }}>
            <div style={{ position: "relative", flexShrink: 0 }}>
              <div style={{
                width: 40, height: 40, borderRadius: "50%",
                background: "rgba(201,169,110,0.15)",
                border: "2px solid rgba(201,169,110,0.45)",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke="rgba(201,169,110,0.8)" strokeWidth="1.5"/><path d="M8 12h8M12 8v8" stroke="rgba(201,169,110,0.8)" strokeWidth="1.5" strokeLinecap="round"/></svg>
              </div>
              <div style={{
                position: "absolute", bottom: 1, right: 1,
                width: 11, height: 11, borderRadius: "50%",
                background: "#22c55e", border: "2px solid rgba(0,14,36,0.95)",
              }} />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontFamily: "'Inter', sans-serif", fontWeight: 600, fontSize: 14, color: BRAND.white, letterSpacing: "0.02em" }}>
                Labarium AI
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 5, marginTop: 2 }}>
                <div style={{ width: 6, height: 6, borderRadius: "50%", background: "#22c55e", animation: "glowPulse 2s ease-in-out infinite" }} />
                <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: "rgba(255,255,255,0.5)", fontWeight: 500 }}>Online · Ask me anything</span>
              </div>
            </div>
            <button
              onClick={toggle}
              style={{
                background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.1)",
                color: "rgba(255,255,255,0.5)", borderRadius: 8,
                width: 28, height: 28, cursor: "pointer", display: "flex",
                alignItems: "center", justifyContent: "center",
                transition: "all 0.2s",
              }}
              onMouseEnter={e => { e.currentTarget.style.color = BRAND.white; e.currentTarget.style.borderColor = "rgba(255,255,255,0.2)"; }}
              onMouseLeave={e => { e.currentTarget.style.color = "rgba(255,255,255,0.5)"; e.currentTarget.style.borderColor = "rgba(255,255,255,0.1)"; }}
            >
              <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                <path d="M1 1l8 8M9 1l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
            </button>
          </div>

          {/* Messages */}
          <div style={{
            flex: 1, overflowY: "auto", padding: "16px 16px 8px",
            display: "flex", flexDirection: "column", gap: 12,
          }}>
            {messages.map((msg, i) => (
              <div
                key={i}
                style={{
                  display: "flex",
                  justifyContent: msg.role === "user" ? "flex-end" : "flex-start",
                }}
              >
                <div style={{
                  maxWidth: "82%",
                  padding: "10px 14px",
                  borderRadius: msg.role === "user" ? "14px 14px 4px 14px" : "14px 14px 14px 4px",
                  background: msg.role === "user"
                    ? `linear-gradient(135deg, ${BRAND.gold}, ${BRAND.goldLight})`
                    : "rgba(255,255,255,0.07)",
                  border: msg.role === "user" ? "none" : "1px solid rgba(255,255,255,0.08)",
                  fontFamily: "'Inter', sans-serif",
                  fontSize: 13,
                  fontWeight: msg.role === "user" ? 500 : 400,
                  color: msg.role === "user" ? BRAND.navyDeep : "rgba(255,255,255,0.85)",
                  lineHeight: 1.65,
                  boxShadow: msg.role === "user" ? "0 4px 16px rgba(201,169,110,0.25)" : "none",
                }}>
                  {msg.text}
                </div>
              </div>
            ))}

            {/* Typing indicator */}
            {loading && (
              <div style={{ display: "flex", justifyContent: "flex-start" }}>
                <div style={{
                  padding: "10px 16px",
                  background: "rgba(255,255,255,0.07)",
                  border: "1px solid rgba(255,255,255,0.08)",
                  borderRadius: "14px 14px 14px 4px",
                  display: "flex", gap: 5, alignItems: "center",
                }}>
                  {[0, 1, 2].map(i => (
                    <div key={i} style={{
                      width: 7, height: 7, borderRadius: "50%",
                      background: "rgba(201,169,110,0.6)",
                      animation: `dotBounce 1.2s ease-in-out ${i * 0.2}s infinite`,
                    }} />
                  ))}
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div style={{
            padding: "10px 12px 14px",
            borderTop: "1px solid rgba(255,255,255,0.06)",
            background: "rgba(0,0,0,0.15)",
            display: "flex", alignItems: "center", gap: 8,
          }}>
            <input
              ref={inputRef}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKey}
              placeholder="Type your question…"
              style={{
                flex: 1,
                background: "rgba(255,255,255,0.06)",
                border: "1px solid rgba(255,255,255,0.1)",
                borderRadius: 12,
                padding: "10px 14px",
                fontFamily: "'Inter', sans-serif",
                fontSize: 13,
                color: BRAND.white,
                outline: "none",
                caretColor: BRAND.gold,
                transition: "border-color 0.2s",
              }}
              onFocus={e => e.target.style.borderColor = "rgba(201,169,110,0.4)"}
              onBlur={e => e.target.style.borderColor = "rgba(255,255,255,0.1)"}
            />
            <button
              onClick={send}
              disabled={!input.trim() || loading}
              style={{
                width: 40, height: 40, borderRadius: 12, flexShrink: 0,
                background: !input.trim() || loading
                  ? "rgba(201,169,110,0.2)"
                  : `linear-gradient(135deg, ${BRAND.gold}, ${BRAND.goldLight})`,
                border: "none",
                color: !input.trim() || loading ? "rgba(201,169,110,0.5)" : BRAND.navyDeep,
                cursor: !input.trim() || loading ? "default" : "pointer",
                display: "flex", alignItems: "center", justifyContent: "center",
                transition: "all 0.2s",
                boxShadow: !input.trim() || loading ? "none" : "0 4px 14px rgba(201,169,110,0.3)",
              }}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M14 8H2M10 4l4 4-4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Floating trigger button */}
      <div style={{ position: "relative" }}>
        {/* Pulse ring — only shows when chat has never been opened */}
        {!hasOpened && (
          <div style={{
            position: "absolute", inset: -4,
            borderRadius: "50%",
            border: `2px solid rgba(201,169,110,0.5)`,
            animation: "pulseRing 2s ease-out infinite",
            pointerEvents: "none",
          }} />
        )}
        <button
          onClick={toggle}
          style={{
            width: 60, height: 60, borderRadius: "50%",
            background: open
              ? "rgba(255,255,255,0.1)"
              : `linear-gradient(135deg, ${BRAND.gold} 0%, ${BRAND.goldLight} 60%, ${BRAND.gold} 100%)`,
            border: open ? `2px solid rgba(201,169,110,0.4)` : "none",
            boxShadow: open
              ? "0 8px 28px rgba(0,0,0,0.4)"
              : "0 8px 28px rgba(201,169,110,0.45), 0 4px 12px rgba(0,0,0,0.3)",
            cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center",
            color: open ? BRAND.gold : BRAND.navyDeep,
            transition: "all 0.3s cubic-bezier(.4,0,.2,1)",
            transform: open ? "rotate(0deg)" : "none",
          }}
          onMouseEnter={e => { if (!open) e.currentTarget.style.transform = "scale(1.08)"; }}
          onMouseLeave={e => { if (!open) e.currentTarget.style.transform = "none"; }}
        >
          {open ? (
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M4 4l12 12M16 4L4 16" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          ) : (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M2 2h20a1 1 0 0 1 1 1v14a1 1 0 0 1-1 1h-14l-5 5v-5H2a1 1 0 0 1-1-1V3a1 1 0 0 1 1-1z"/>
            </svg>
          )}
        </button>

        {/* Unread dot */}
        {!hasOpened && (
          <div style={{
            position: "absolute", top: 0, right: 0,
            width: 14, height: 14, borderRadius: "50%",
            background: "#10b981",
            border: "2px solid rgba(0,14,36,1)",
            animation: "glowPulse 2s ease-in-out infinite",
          }} />
        )}
      </div>
    </div>
  );
}
